/**
 * Making an uploaded filename safe to sit in a Storage object path.
 *
 * Its own module because the regular expressions want to be read carefully,
 * and because the security-relevant part is easy to lose in a file about
 * paths: a slash in a filename does not just look untidy, it creates a folder
 * — and can push an object out of the prefix that storage.rules protects.
 */

/** Characters that would change the shape of the path, or break tooling. */
const PATH_UNSAFE = /[/\\?%*:|"<>]/g;

/** C0 controls and DEL. Legal in some filesystems, hostile everywhere else. */
// eslint-disable-next-line no-control-regex -- removing control characters is the point
const CONTROL = new RegExp('[\u0000-\u001f\u007f]', 'g');

/** Combining marks left over from NFKD decomposition. */
const COMBINING = /\p{M}/gu;

export function sanitizeFileName(name: string): string {
  const cleaned = name
    // Decompose and drop the accents, so two spellings of "Café.pdf" — one
    // precomposed, one combining — do not become two different objects.
    .normalize('NFKD')
    .replace(COMBINING, '')
    .replace(PATH_UNSAFE, '-')
    .replace(CONTROL, '')
    // Collapse runs of whitespace, so "Lecture 1.pdf" and "Lecture  1.pdf"
    // stop looking identical in a list while being different files.
    .replace(/\s+/g, ' ')
    .trim();

  const safe = cleaned || 'file';

  // Object names may be far longer than this; the cap is for a readable
  // interface, not for the storage layer.
  return safe.length > 120 ? safe.slice(0, 120) : safe;
}
